import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { RENDERERS } from '../components/LandingBuilder/templates';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export default function LandingPagePublic() {
  const { slug }          = useParams();
  const [page, setPage]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess]       = useState(false);
  const visitTracked = useRef(false);

  useEffect(() => {
    // Fetch page config
    fetch(`${BASE}/api/lp/${slug}`)
      .then(r => { if (!r.ok) throw new Error('not_found'); return r.json(); })
      .then(data => { setPage(data); setLoading(false); })
      .catch(() => { setNotFound(true); setLoading(false); });
  }, [slug]);

  // Track visit once
  useEffect(() => {
    if (!page || visitTracked.current) return;
    visitTracked.current = true;
    const src = (new URLSearchParams(window.location.search).get('src') || '').toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 30);
    fetch(`${BASE}/api/lp/${slug}/visit${src ? `?src=${src}` : ''}`, { method: 'POST' }).catch(() => {});
  }, [page, slug]);

  // SEO title
  useEffect(() => {
    if (page?.title) document.title = page.title;
  }, [page]);

  const handleSubmit = async (formData) => {
    setSubmitting(true);
    try {
      const res = await fetch(`${BASE}/api/lp/${slug}/submit`, {
        method: 'POST',
        // Accept JSON : sans ça, Laravel REDIRIGE (302) sur erreur de validation
        // au lieu de renvoyer le message — découvert au test E2E 24.07
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          ...formData,
          src: (new URLSearchParams(window.location.search).get('src') || '').toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 30) || undefined,
          p: (new URLSearchParams(window.location.search).get('p') || '').toUpperCase().replace(/[^A-Z0-9-]/g, '').slice(0, 24) || undefined,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || 'Erreur lors de la soumission');
      setSuccess({ coupon: json.coupon_code || null, rewardText: json.reward_text || null, referralCode: json.referral_code || null, shareUrl: `${window.location.origin}${window.location.pathname}` });
    } catch (e) {
      alert(e.message || 'Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#F5F3FF', flexDirection: 'column', gap: 16 }}>
        <div style={{ width: 44, height: 44, border: '4px solid #EDE9FF', borderTopColor: '#6428F4', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (notFound) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#F9FAFB', flexDirection: 'column', gap: 16, fontFamily: 'Inter,sans-serif' }}>
        <div style={{ fontSize: 48 }}>🔍</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: '#1F2937' }}>Page introuvable</div>
        <div style={{ fontSize: 15, color: '#6B7280' }}>Cette landing page n'existe pas ou a été dépubliée.</div>
      </div>
    );
  }

  // Concours clos (gagnant tiré) → écran de fin chaleureux, plus de formulaire
  if (page.closed) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: page.config?.bg_color || '#FDF4FF', flexDirection: 'column', gap: 14, padding: 24, textAlign: 'center', fontFamily: 'Inter,Arial,sans-serif' }}>
        <div style={{ fontSize: 56 }}>🏁</div>
        <div style={{ fontSize: 24, fontWeight: 900, color: '#1F2937' }}>Le concours est terminé !</div>
        <div style={{ fontSize: 15, color: '#6B7280', maxWidth: 420, lineHeight: 1.6 }}>
          Le tirage au sort a eu lieu et le gagnant a été contacté personnellement.
          Merci à tous les participants — et restez connectés, {page.config?.company_name || 'l\'équipe'} remet ça bientôt 🍀
        </div>
      </div>
    );
  }

  const Comp = RENDERERS[page.template] || RENDERERS.lead_magnet;
  return (
    <>
      <Comp
        config={page.config}
        onSubmit={handleSubmit}
        submitting={submitting}
        success={success}
        preview={false}
      />
      {/* White-label : mention discrète et cliquable — l'entreprise cliente reste en avant */}
      <div style={{ textAlign: 'center', padding: '14px 0 18px', fontSize: 11, color: '#B8B4C8', fontFamily: 'inherit' }}>
        Propulsé par{' '}
        <a href="https://beeappee.com" target="_blank" rel="noopener noreferrer"
           style={{ color: '#8B84A8', fontWeight: 700, textDecoration: 'none' }}>beeappee</a>
      </div>
    </>
  );
}
